/**
 * Payment settings API client (BFF: `/api/v1/store/settings/payments`).
 *
 * Covers the payment settings document plus payment-provider CRUD and connection
 * testing. Provider secrets are write-only on upsert and never returned.
 */
import { apiClient } from '@/shared/api';
import type {
  PaymentSettings,
  ProviderSummary,
  ProviderUpsertRequest,
  TestConnectionResult,
  UpdatePaymentSettingsRequest,
} from '../model/settingsTypes';

const BASE = '/api/v1/store/settings/payments';

export const getPaymentSettings = async (): Promise<PaymentSettings> =>
  (await apiClient.get<PaymentSettings>(BASE)).data;

export const updatePaymentSettings = async (
  body: UpdatePaymentSettingsRequest,
): Promise<PaymentSettings> => (await apiClient.put<PaymentSettings>(BASE, body)).data;

export const listPaymentProviders = async (): Promise<ProviderSummary[]> =>
  (await apiClient.get<ProviderSummary[]>(`${BASE}/providers`)).data;

export const createPaymentProvider = async (
  body: ProviderUpsertRequest,
): Promise<ProviderSummary> =>
  (await apiClient.post<ProviderSummary>(`${BASE}/providers`, body)).data;

export const updatePaymentProvider = async (
  providerId: string,
  body: ProviderUpsertRequest,
): Promise<ProviderSummary> =>
  (await apiClient.put<ProviderSummary>(`${BASE}/providers/${providerId}`, body)).data;

export const deletePaymentProvider = async (providerId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/providers/${providerId}`);
};

export const testPaymentProviderConnection = async (
  providerId: string,
): Promise<TestConnectionResult> =>
  (await apiClient.post<TestConnectionResult>(`${BASE}/providers/${providerId}/test-connection`))
    .data;
